import type { defaultSnapProps } from "react-spring-bottom-sheet/dist/types";

import { useBottomDrawer } from "../BottomDrawerContext";

export const useBottomDrawerHelpers = () => {
  // Hooks
  const {
    minimize,
    setContent,
    setHeader,
    setFooter,
    setSnapPoints,
    bottomDrawerRef,
    overlayRef,
    topOffset,
    setIsLocked,
  } = useBottomDrawer();

  const snapToBottomDrawer = (
    numberOrCallback: number | ((state: defaultSnapProps) => number),
    options?: {
      source?: string;
      velocity?: number;
    },
    delay?: number,
  ) => {
    setTimeout(() => {
      bottomDrawerRef?.snapTo(numberOrCallback, {
        ...options,
        velocity: 0.5,
      });
    }, delay || 100);
  };

  const snapToMinBottomDrawer = () => {
    snapToBottomDrawer(({ snapPoints }) => Math.min(...snapPoints));
  };

  const snapToMaxBottomDrawer = (
    { delay }: { delay?: number } = { delay: 100 },
  ) => {
    snapToBottomDrawer(({ snapPoints }) => Math.max(...snapPoints), {}, delay);
  };

  const snapToFirstBottomDrawer = () => {
    snapToBottomDrawer(({ snapPoints }) => snapPoints[0]);
  };

  const snapToLastBottomDrawer = () => {
    snapToBottomDrawer(({ snapPoints }) => snapPoints[snapPoints.length - 1]);
  };

  const lockToTopBottomDrawer = (additionalOffset: number) => {
    if (!overlayRef?.current) return;

    overlayRef.current.style.top = `${topOffset + additionalOffset}px`;
    overlayRef.current.style.bottom = `unset`;

    setSnapPoints(() => ({ maxHeight }) => {
      return [maxHeight - topOffset - additionalOffset];
    });

    setIsLocked(true);
  };

  const unlockBottomDrawer = () => {
    if (!overlayRef?.current) return;

    overlayRef.current.style.top = `unset`;
    overlayRef.current.style.bottom = `0`;

    setIsLocked(false);
  };

  /* TODO WTF??? react-spring-bottom-sheet lib does not allow
  the bottom sheet to render on first load if the content is an empty
  fragment, even when having a valid header and/or footer. This fix is here
  to be able to load the map viewer with a poi selected with no description */
  const setEmptyContent = () => {
    setContent(
      <div
        className="bottom-drawer__empty-content"
        aria-hidden
        style={{ height: 1, width: 0 }}
      />,
    );
  };

  const hideAndClearBottomDrawer = () => {
    setHeader(null);
    setContent(null);
    setFooter(null);
    setSnapPoints(null);
  };

  const minimizeAndClearBottomDrawer = () => {
    minimize();
    setHeader(null);
    setFooter(null);
    setSnapPoints(() => () => 0);
    snapToBottomDrawer(() => 0);
    setEmptyContent();
  };

  return {
    hideAndClearBottomDrawer,
    lockToTopBottomDrawer,
    minimizeAndClearBottomDrawer,
    setEmptyContent,
    snapToBottomDrawer,
    snapToFirstBottomDrawer,
    snapToLastBottomDrawer,
    snapToMaxBottomDrawer,
    snapToMinBottomDrawer,
    unlockBottomDrawer,
  };
};
